import {FIELD_BIRD_TEXTURE_KEY, FIELD_EAGLE_TEXTURE_KEY} from '../../configs/fieldAmbience';

/**
 * Текстуры птицы и орла для антуража поля (вид сверху). Картинок в public нет —
 * пиксели считаются кодом и регистрируются через textures.addUint8Array.
 * Облака — готовый лист картинок (см. FIELD_CLOUDS_SHEET в configs/fieldAmbience).
 *
 * ПОЧЕМУ НЕ CANVAS: canvas-текстура после потери GPU-контекста (мобильные в
 * фоне) остается пустой, а текстура из массива восстанавливается из памяти
 * (подробно — utils/sprites/generateOutlineTexture).
 *
 * ФОРМАТ: RGBA построчно сверху вниз, цвет НЕ умножен на альфу — на альфу его
 * умножает браузер при загрузке в видеокарту (подробно —
 * utils/effects/shotProjectileTextures). Размеры — степени двойки.
 *
 * Птица — чисто белая, форма только в альфе: цвет ей дает tint спрайта (стаи
 * бывают белые, черные и коричневые). Орел — цветной: бурый, с белой головой.
 */

export const BIRD_TEXTURE_SIZE = {width: 32, height: 32};
// Размах крыльев в пикселях текстуры: по нему считается масштаб спрайта
export const BIRD_TEXTURE_WINGSPAN = 30;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

/**
 * Покрытие точки «каплей» — отрезком, который сужается от одного конца к
 * другому: 1 внутри, 0 снаружи, на кромке — сглаживание в пиксель
 */
const taperedSegmentCoverage = (px, py, [ax, ay, radiusA], [bx, by, radiusB]) => {
  const abX = bx - ax;
  const abY = by - ay;
  const share = clamp01(((px - ax) * abX + (py - ay) * abY) / (abX * abX + abY * abY));
  const distance = Math.hypot(px - (ax + abX * share), py - (ay + abY * share));
  return clamp01(radiusA + (radiusB - radiusA) * share - distance + 0.5);
};

/**
 * Птица, вид сверху, смотрит ВПРАВО (угол 0): тело вдоль оси X, крылья — в
 * стороны по оси Y. Взмах сцена показывает сжатием спрайта по Y: сверху взмах
 * виден как укорочение размаха.
 */
export const buildBirdPixels = () => {
  const {width, height} = BIRD_TEXTURE_SIZE;
  const pixels = new Uint8Array(width * height * 4);
  const centerY = (height - 1) / 2;
  // Кончики крыльев — капли радиусом 0.6: размах выходит на пиксель шире
  // расстояния между их центрами
  const halfSpan = (BIRD_TEXTURE_WINGSPAN - 1) / 2;

  // Каждая часть — капля [x, y, радиус] -> [x, y, радиус]. Тело короткое, без
  // длинного носа и прямых крыльев: иначе силуэт читается самолетом
  const parts = [
    // Тело от хвоста к голове
    [[11.5, centerY, 1.2], [19, centerY, 2.1]],
    // Голова
    [[19, centerY, 2.1], [22, centerY, 1.3]],
    // Хвост — короткий веер
    [[11.5, centerY, 1.2], [8, centerY - 1.7, 0.7]],
    [[11.5, centerY, 1.2], [8, centerY + 1.7, 0.7]],
  ];
  [-1, 1].forEach((side) => {
    // Плечо — сгиб — кончик: до сгиба крыло идет чуть вперед, дальше резко
    // уходит назад и сужается — излом, как у чайки
    const wrist = [20, centerY + side * halfSpan * 0.46, 2.3];
    parts.push([[17.5, centerY + side * 1.4, 2.7], wrist]);
    parts.push([wrist, [12.5, centerY + side * halfSpan, 0.6]]);
  });

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = parts.reduce(
        (largest, [from, to]) => Math.max(largest, taperedSegmentCoverage(x, y, from, to)), 0
      );
      if (alpha > 0) {
        // Цвет 0..255 как есть (на альфу его умножит браузер), альфа 0..1
        const offset = (y * width + x) * 4;
        pixels[offset] = 255;
        pixels[offset + 1] = 255;
        pixels[offset + 2] = 255;
        pixels[offset + 3] = Math.round(255 * alpha);
      }
    }
  }

  return pixels;
};

export const EAGLE_TEXTURE_SIZE = {width: 64, height: 64};
// Размах крыльев орла в пикселях текстуры
export const EAGLE_TEXTURE_WINGSPAN = 60;

// Цвета орла: бурые тело и крылья (маховые перья темнее), белая голова
export const EAGLE_COLORS = {
  body: [0x66, 0x44, 0x26],
  wing: [0x7d, 0x54, 0x30],
  feathers: [0x4d, 0x32, 0x1b],
  head: [0xf6, 0xf3, 0xea],
  beak: [0xe8, 0xb5, 0x45],
};

/**
 * Орел, вид сверху, смотрит ВПРАВО (угол 0): тело вдоль оси X, широкие
 * крылья с «пальцами» на концах — в стороны по оси Y. Взмах сцена показывает
 * сжатием спрайта по Y, как у птицы.
 */
export const buildEaglePixels = () => {
  const {width, height} = EAGLE_TEXTURE_SIZE;
  const pixels = new Uint8Array(width * height * 4);
  const centerY = (height - 1) / 2;
  // Кончики «пальцев» — капли радиусом 0.8: размах выходит на пиксель шире
  // расстояния между их центрами
  const halfSpan = (EAGLE_TEXTURE_WINGSPAN - 1) / 2 - 0.3;
  const {body, wing, feathers, head, beak} = EAGLE_COLORS;

  // Каждая часть — капля [x, y, радиус] -> [x, y, радиус] своего цвета; части
  // ложатся одна поверх другой в этом порядке
  const parts = [
    // Хвост — веер
    [[25, centerY, 2.3], [15.5, centerY - 4.4, 1.7], feathers],
    [[25, centerY, 2.3], [15.5, centerY + 4.4, 1.7], feathers],
    [[25, centerY, 2.3], [14.5, centerY, 2], feathers],
  ];
  [-1, 1].forEach((side) => {
    // Маховые перья: широкая задняя часть крыла. Крыло орла — «доска»: широкое
    // по всей длине, а не клин, как у чайки
    parts.push([[28.5, centerY + side * 4, 6.4], [27.5, centerY + side * (halfSpan - 6), 5], feathers]);
    // «Пальцы» на конце крыла — веером, от переднего края к заднему
    [0, 1, 2, 3, 4].forEach((finger) => {
      parts.push([
        [33 - finger * 2.3, centerY + side * (halfSpan - 6.5), 1.6],
        [37.5 - finger * 3.6, centerY + side * (halfSpan - Math.abs(finger - 1.5) * 0.9), 0.8],
        feathers,
      ]);
    });
    // Передняя кромка крыла светлее: плечо — сгиб — конец
    const wrist = [37, centerY + side * halfSpan * 0.55, 3.6];
    parts.push([[35.5, centerY + side * 3.5, 4], wrist, wing]);
    parts.push([wrist, [34.5, centerY + side * (halfSpan - 6), 2.6], wing]);
  });
  parts.push(
    // Тело от хвоста к плечам
    [[24.5, centerY, 2.7], [37, centerY, 4], body],
    // Белая голова и клюв
    [[39, centerY, 3.3], [43.5, centerY, 2.5], head],
    [[45.5, centerY, 1.3], [47.3, centerY, 0.6], beak]
  );

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let alpha = 0;
      const color = [0, 0, 0];
      for (let index = 0; index < parts.length; index += 1) {
        const [from, to, partColor] = parts[index];
        const coverage = taperedSegmentCoverage(x, y, from, to);
        if (coverage > 0) {
          // Часть ложится поверх уже нарисованного
          const nextAlpha = coverage + alpha * (1 - coverage);
          for (let channel = 0; channel < 3; channel += 1) {
            color[channel] = (partColor[channel] * coverage + color[channel] * alpha * (1 - coverage)) / nextAlpha;
          }
          alpha = nextAlpha;
        }
      }
      if (alpha > 0) {
        // Цвет 0..255 как есть (на альфу его умножит браузер), альфа 0..1
        const offset = (y * width + x) * 4;
        pixels[offset] = Math.round(color[0]);
        pixels[offset + 1] = Math.round(color[1]);
        pixels[offset + 2] = Math.round(color[2]);
        pixels[offset + 3] = Math.round(255 * alpha);
      }
    }
  }

  return pixels;
};

/**
 * Зарегистрировать текстуру из кода в сцене (один раз: готовую не трогает).
 *
 * @returns {boolean} на месте ли текстура
 */
const ensureTexture = (scene, key, buildPixels, {width, height}, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }
  if (textures.exists(key)) {
    return true;
  }

  const texture = textures.addUint8Array(key, buildPixels(), width, height);
  if (!texture) {
    return false;
  }
  if (linearFilter !== undefined) {
    texture.setFilter?.(linearFilter);
  }
  return true;
};

/**
 * Зарегистрировать текстуру птицы в сцене (один раз: готовую не трогает).
 *
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR: у текстуры
 *   из массива по умолчанию NEAREST, и повернутая птица пошла бы ступеньками
 * @returns {boolean} на месте ли текстура; false — птиц не будет
 */
export const ensureBirdTexture = (scene, options) => (
  ensureTexture(scene, FIELD_BIRD_TEXTURE_KEY, buildBirdPixels, BIRD_TEXTURE_SIZE, options)
);

/**
 * Зарегистрировать текстуру орла в сцене (см. ensureBirdTexture).
 *
 * @returns {boolean} на месте ли текстура; false — орлов не будет
 */
export const ensureEagleTexture = (scene, options) => (
  ensureTexture(scene, FIELD_EAGLE_TEXTURE_KEY, buildEaglePixels, EAGLE_TEXTURE_SIZE, options)
);
